import type { EnvConfig } from "../types";
function validateEnv(): EnvConfig {
  const requiredVars = {
    API_URL: import.meta.env.VITE_API_URL,
    AZURE_CLIENT_ID: import.meta.env.VITE_AZURE_CLIENT_ID,
    AZURE_TENANT: import.meta.env.VITE_AZURE_TENANT,
  };

  const missing: string[] = [];

  Object.entries(requiredVars).forEach(([key, value]) => {
    if (!value || value === undefined || value === "") {
      missing.push(`VITE_${key}`);
    }
  });

  if (missing.length > 0) {
    // Show a user-friendly error UI
    const errorHtml = `
      <div style="
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(0, 0, 0, 0.85);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 10000;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
      ">
        <div style="
          background: white;
          border-radius: 12px;
          padding: 32px;
          max-width: 600px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        ">
          <div style="display: flex; align-items: center; margin-bottom: 20px;">
            <div style="
              width: 48px;
              height: 48px;
              border-radius: 50%;
              background: #ff4d4f;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-right: 16px;
            ">
              <span style="color: white; font-size: 24px; font-weight: bold;">!</span>
            </div>
            <h1 style="
              margin: 0;
              font-size: 24px;
              color: #262626;
              font-weight: 600;
            ">Environment Configuration Error</h1>
          </div>
          
          <p style="
            color: #595959;
            font-size: 16px;
            line-height: 1.6;
            margin-bottom: 20px;
          ">The following required environment variables are missing:</p>
          
          <div style="
            background: #fff2f0;
            border: 1px solid #ffccc7;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 24px;
          ">
            ${missing
              .map(
                (v) => `<div style="
              color: #cf1322;
              font-family: 'Courier New', monospace;
              font-size: 14px;
              font-weight: 500;
              padding: 4px 0;
            ">• ${v}</div>`,
              )
              .join("")}
          </div>
          
          <div style="
            background: #f0f5ff;
            border-left: 4px solid #1890ff;
            padding: 16px;
            border-radius: 4px;
            margin-bottom: 20px;
          ">
            <h3 style="
              margin: 0 0 12px 0;
              color: #0050b3;
              font-size: 14px;
              font-weight: 600;
            ">How to fix:</h3>
            <ol style="
              margin: 0;
              padding-left: 20px;
              color: #262626;
              font-size: 14px;
              line-height: 1.8;
            ">
              <li>Open the <code style="background: white; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace;">.env</code> file in the project root</li>
              <li>Uncomment one of the VITE_API_URL options:
                <ul style="margin: 8px 0; list-style: none; padding-left: 0;">
                  <li style="padding: 4px 0;">• <strong>Local Development:</strong> <code style="background: white; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace;">VITE_API_URL=http://localhost:4000</code></li>
                  <li style="padding: 4px 0;">• <strong>Dev Server:</strong> <code style="background: white; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace;">VITE_API_URL=https://sm-dev.api.miro.zoracom.com</code></li>
                  <li style="padding: 4px 0;">• <strong>Production:</strong> <code style="background: white; padding: 2px 6px; border-radius: 3px; font-family: 'Courier New', monospace;">VITE_API_URL=https://sm.api.miro.zoracom.com</code></li>
                </ul>
              </li>
              <li>Save the file and refresh this page</li>
            </ol>
          </div>
          
          <button onclick="window.location.reload()" style="
            width: 100%;
            padding: 12px 24px;
            background: #1890ff;
            color: white;
            border: none;
            border-radius: 6px;
            font-size: 16px;
            font-weight: 500;
            cursor: pointer;
            transition: background 0.2s;
          " onmouseover="this.style.background='#40a9ff'" onmouseout="this.style.background='#1890ff'">
            Reload Page
          </button>
        </div>
      </div>
    `;

    // Insert error UI into DOM
    if (typeof document !== "undefined") {
      const errorDiv = document.createElement("div");
      errorDiv.innerHTML = errorHtml;
      document.body.appendChild(errorDiv);
    }

    // Also log detailed error to console for developers
    console.group(
      "%c⚠️ Environment Configuration Error",
      "color: #ff4d4f; font-size: 16px; font-weight: bold;",
    );
    console.error("Missing required environment variables:");
    missing.forEach((v) => console.error(`  • ${v}`));
    console.log("\n%cHow to fix:", "font-weight: bold; color: #1890ff;");
    console.log("1. Open the .env file in the project root");
    console.log("2. Uncomment one of the VITE_API_URL options");
    console.log("3. Save the file and refresh the page");
    console.groupEnd();

    throw new Error(
      `Missing required environment variables:\n${missing.join("\n")}\n\nPlease check your .env file and uncomment one of the VITE_API_URL options.`,
    );
  }

  const NODE_ENV = import.meta.env.MODE || "development";

  return {
    API_URL: requiredVars.API_URL,
    AZURE_CLIENT_ID: requiredVars.AZURE_CLIENT_ID,
    AZURE_TENANT: requiredVars.AZURE_TENANT,
    NODE_ENV,
    isDevelopment: NODE_ENV === "development",
    isProduction: NODE_ENV === "production",
  };
}

export const env = validateEnv();

if (env.isDevelopment) {
  console.log("[ENV] Configuration loaded:", {
    API_URL: env.API_URL,
    AZURE_CLIENT_ID: env.AZURE_CLIENT_ID.substring(0, 8) + "...",
    AZURE_TENANT: env.AZURE_TENANT.substring(0, 8) + "...",
    NODE_ENV: env.NODE_ENV,
  });
}
